import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, KeyRound, Search, Undo2, UserRoundCheck } from 'lucide-react';

import { useApiErrorMessage } from '@/shared/i18n/useApiErrorMessage';
import { Button, Skeleton } from '@/shared/ui';

import type { ImportChange, ImportRunSummary } from '../model/types';
import { useImportChanges, useImportRuns } from '../queries/useImport';
import styles from './Import.module.css';

/** Rodzaje pozycji raportu — te same wartości, co po stronie serwera. */
const KINDS = [
  'elementAdded',
  'elementFieldUpdated',
  'relationAdded',
  'keyAdopted',
  'ownerAdded',
  'ownerReadded',
  'relationSkipped',
] as const;

type Kind = (typeof KINDS)[number];

const PAGE = 50;

interface Props {
  run: ImportRunSummary;
  onBack: () => void;
  onRollback: () => void;
}

/**
 * Czy TEN przebieg da się jeszcze wycofać.
 *
 * ⛔ Wyłącznie **najnowszy zamknięty** (R24) — warunek strukturalny, nie zegarowy: kasuje całą
 * rodzinę stanów „import 1 utworzył, import 2 zmienił, cofam import 1".
 *
 * ⚠ Liczymy to na froncie tylko po to, żeby **nie rysować drzwi, które i tak są zamknięte** —
 * regułę egzekwuje serwer i on jest tu jedynym strażnikiem.
 */
function daSieWycofac(run: ImportRunSummary, wszystkie: readonly ImportRunSummary[]): boolean {
  if (run.state !== 'committed') {
    return false;
  }

  return !wszystkie.some((inny) => inny.id > run.id && inny.state !== 'open');
}

/**
 * Raport z przebiegu (K6).
 *
 * ⭐⭐ <b>Raport przy stu tysiącach elementów NIE JEST listą, tylko ZAPYTANIEM</b> — i to jest
 * jedyna rzecz, którą trzeba o tym ekranie wiedzieć. Jeden przebieg produkuje ponad
 * siedemdziesiąt tysięcy pozycji: nie da się ich przejrzeć ani wydrukować, a jedyne sensowne
 * użycie to pytanie („co się stało z <c>svc/payments</c>", „gdzie zmienił się typ").
 * Dlatego filtr i fraza stoją na wierzchu, a lista jest ich <b>wynikiem</b>, nie treścią ekranu.
 *
 * ⛔ Grupy zwijane, znane z listy konfliktów, byłyby tu pomyłką: tam nagłówek grupy niósł
 * <b>akcje hurtowe</b>, więc był miejscem, gdzie się coś robi. W raporcie nie ma czego robić —
 * grupa byłaby tylko szufladą na dwadzieścia cztery tysiące wierszy.
 */
export function ImportReport({ run, onBack, onRollback }: Props) {
  const { t, i18n } = useTranslation();
  const describeError = useApiErrorMessage();

  const [kind, setKind] = useState<Kind | null>(null);
  const [query, setQuery] = useState('');
  const [term, setTerm] = useState('');
  const [offset, setOffset] = useState(0);

  const runs = useImportRuns();
  const mozliweWycofanie = daSieWycofac(run, runs.data ?? []);

  const page = useImportChanges(run.id, {
    ...(kind ? { kind } : {}),
    ...(term ? { query: term } : {}),
    offset,
    limit: PAGE,
  });

  const format = new Intl.NumberFormat(i18n.language);

  return (
    <div className={styles['screen']}>
      <header className={styles['head']}>
        <div className={styles['reportBar']}>
          <Button variant="ghost" onClick={onBack}>
            <ArrowLeft size={15} aria-hidden="true" />
            {t('import.report.back')}
          </Button>

          {/* ⛔ Wejście w jedyną nieodwracalną operację w produkcie — pokazywane wyłącznie
              wtedy, gdy naprawdę jest możliwe (R24). */}
          {mozliweWycofanie ? (
            <Button variant="ghost" onClick={onRollback}>
              <Undo2 size={15} aria-hidden="true" />
              {t('import.report.rollback')}
            </Button>
          ) : null}
        </div>
        <h1 className={styles['title']}>
          {t('import.report.title', { file: run.fileName ?? t('import.run.noName') })}
        </h1>
        <p className={styles['lead']}>
          {t('import.report.lead', {
            source: run.source,
            state: t(`import.runState.${run.state}`),
          })}
        </p>
      </header>

      <form
        className={styles['filters']}
        onSubmit={(event) => {
          event.preventDefault();
          setTerm(query.trim());
          setOffset(0);
        }}
      >
        <label className={styles['search']}>
          <Search size={15} aria-hidden="true" />
          <span className={styles['srOnly']}>{t('import.report.searchLabel')}</span>
          <input
            className={styles['searchInput']}
            type="search"
            value={query}
            placeholder={t('import.report.searchPlaceholder')}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>

        <Button variant="ghost" type="submit">
          {t('import.report.search')}
        </Button>

        <fieldset className={styles['chips']}>
          <legend className={styles['srOnly']}>{t('import.report.kindFilter')}</legend>
          <button
            type="button"
            className={styles['chipButton']}
            aria-pressed={kind === null}
            onClick={() => {
              setKind(null);
              setOffset(0);
            }}
          >
            {t('import.report.allKinds')}
          </button>

          {KINDS.map((value) => (
            <button
              key={value}
              type="button"
              className={styles['chipButton']}
              aria-pressed={kind === value}
              onClick={() => {
                setKind(kind === value ? null : value);
                setOffset(0);
              }}
            >
              {t(`import.changeKind.${value}`)}
            </button>
          ))}
        </fieldset>
      </form>

      {page.isPending ? (
        <div aria-busy="true">
          <Skeleton height="3rem" announce />
          <Skeleton height="3rem" />
          <Skeleton height="3rem" />
        </div>
      ) : page.isError ? (
        <p className={styles['rowsError']} role="alert">
          {describeError(page.error)}
        </p>
      ) : page.data.items.length === 0 ? (
        <p className={styles['emptyRuns']}>{t('import.report.empty')}</p>
      ) : (
        <>
          <div className={styles['tableWrap']}>
            <table className={styles['table']}>
              <thead>
                <tr>
                  <th scope="col">{t('import.report.column.element')}</th>
                  <th scope="col">{t('import.report.column.what')}</th>
                  <th scope="col">{t('import.report.column.change')}</th>
                </tr>
              </thead>
              <tbody>
                {page.data.items.map((change) => (
                  <ChangeRow
                    key={`${change.kind}-${change.externalKey}-${change.field ?? ''}-${change.elementId ?? ''}-${change.newValue ?? ''}`}
                    change={change}
                  />
                ))}
              </tbody>
            </table>
          </div>

          <div className={styles['pager']}>
            <Button variant="ghost" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
              {t('import.conflicts.prev')}
            </Button>
            <span className={styles['pagerInfo']}>
              {t('import.conflicts.range', {
                from: offset + 1,
                to: Math.min(offset + PAGE, page.data.total),
                total: format.format(page.data.total),
              })}
            </span>
            <Button
              variant="ghost"
              disabled={offset + PAGE >= page.data.total}
              onClick={() => setOffset(offset + PAGE)}
            >
              {t('import.conflicts.next')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function ChangeRow({ change }: { change: ImportChange }) {
  const { t } = useTranslation();

  // ⭐ Dwa rodzaje dostają IKONĘ, bo niosą coś, czego liczba nie powie: adopcja klucza znaczy
  // „wycofanie tego nie usunie", a ponowne dopisanie właściciela — „ktoś to wcześniej zdjął
  // ręcznie, a import mu to odwrócił".
  const Icon =
    change.kind === 'keyAdopted' ? KeyRound : change.kind === 'ownerReadded' ? UserRoundCheck : null;

  return (
    <tr data-kind={change.kind}>
      <td>
        <span className={styles['rowKey']}>{change.externalKey || '—'}</span>
      </td>
      <td>
        <span className={styles['changeKind']}>
          {Icon ? <Icon size={13} aria-hidden="true" /> : null}
          {t(`import.changeKind.${change.kind}`, { defaultValue: change.kind })}
          {change.field ? (
            <span className={styles['changeField']}>
              {t(`import.field.${change.field}`, { defaultValue: change.field })}
            </span>
          ) : null}
        </span>
      </td>
      <td>
        {/* ⭐ Przy relacji pokazujemy TYTUŁ drugiego końca — gołe id jest liczbą,
            której człowiek nie ma jak sprawdzić. */}
        {change.relatedTitle ? (
          <span className={styles['valueNew']}>{change.relatedTitle}</span>
        ) : change.reasonKey ? (
          <span className={styles['factHint']}>{t(change.reasonKey, { defaultValue: change.reasonKey })}</span>
        ) : change.oldValue !== null ? (
          <span className={styles['diffValues']}>
            <span className={styles['valueOld']}>{change.oldValue}</span>
            <ArrowRight size={13} aria-hidden="true" className={styles['diffArrow']} />
            <span className={styles['valueNew']}>{change.newValue ?? '—'}</span>
          </span>
        ) : (
          <span className={styles['valueNew']}>{change.newValue ?? '—'}</span>
        )}
      </td>
    </tr>
  );
}
